import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

// Phase AUTH-S — sliding-window refresh tunables. The token's expiry comes
// from the JWT itself; we only poll while the tab is visible to avoid burning
// requests on idle background tabs.
const POLL_INTERVAL_MS = 60 * 1000;                  // 1 min cadence
const REFRESH_THRESHOLD_MS = 30 * 60 * 1000;          // refresh when <30m left
const WARNING_THRESHOLD_MS = 2 * 60 * 1000;           // show modal when ≤2m left
const HARD_EXPIRED_GRACE_MS = 1 * 1000;               // tiny grace for clock skew

function decodeJwt(token) {
    if (!token || typeof token !== "string") return null;
    try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
        const json = atob(padded);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = not loaded yet OR unauth
    const [loading, setLoading] = useState(true);
    // Phase AUTH-S — warning modal state. Surfaces ≤2m before token expiry.
    const [sessionWarning, setSessionWarning] = useState(false);
    const refreshingRef = useRef(false);

    const loadMe = useCallback(async () => {
        const token = localStorage.getItem("rp_token");
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            localStorage.removeItem("rp_token");
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadMe();
    }, [loadMe]);

    const login = useCallback(async (email, password, remember_me = false) => {
        const { data } = await api.post("/auth/login", { email, password, remember_me });
        localStorage.setItem("rp_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const signup = useCallback(async (email, password, full_name) => {
        const { data } = await api.post("/auth/signup", { email, password, full_name });
        localStorage.setItem("rp_token", data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        // Phase AUTH-S — best-effort server-side revocation so the same JWT
        // can't be replayed from another device after sign-out. Always wipe
        // local state regardless of network outcome.
        try {
            await api.post("/auth/logout");
        } catch {
            /* token may already be expired/invalid — ignore */
        }
        localStorage.removeItem("rp_token");
        localStorage.removeItem("sa_original_token");
        localStorage.removeItem("sa_return_path");
        setUser(null);
        setSessionWarning(false);
    }, []);

    const refreshUser = useCallback(async () => {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return data;
    }, []);

    // Phase AUTH-S — sliding-window refresh. Mints a new token preserving
    // jti + max_session_exp (server enforces the 30d cap on Remember-me
    // sessions). Returns true on success so the warning modal can dismiss.
    const refreshSession = useCallback(async () => {
        if (refreshingRef.current) return false;
        refreshingRef.current = true;
        try {
            const { data } = await api.post("/auth/refresh");
            localStorage.setItem("rp_token", data.token);
            setUser(data.user);
            setSessionWarning(false);
            return true;
        } catch {
            return false;
        } finally {
            refreshingRef.current = false;
        }
    }, []);

    // Phase H.5 — start a super-admin-initiated impersonation session. Saves
    // the super admin token under `sa_original_token` so the API interceptor
    // and the "Return" button can both fall back to it.
    const startImpersonation = useCallback(async (userId, returnPath = null) => {
        const originalToken = localStorage.getItem("rp_token");
        const { data } = await api.post(`/admin/impersonate/${userId}`);
        if (originalToken) localStorage.setItem("sa_original_token", originalToken);
        if (returnPath) localStorage.setItem("sa_return_path", returnPath);
        localStorage.setItem("rp_token", data.token);
        // Refresh /me so the banner + route guards see the impersonation role.
        const { data: me } = await api.get("/auth/me");
        setUser(me);
        return data.impersonating;
    }, []);

    // Phase H.5 — stop impersonation: swap back to a fresh super-admin token.
    // Returns the landing path the UI should navigate to.
    const stopImpersonation = useCallback(async () => {
        let landing = localStorage.getItem("sa_return_path") || "/admin";
        try {
            const { data } = await api.post("/admin/impersonate/stop");
            localStorage.setItem("rp_token", data.token);
            setUser(data.user);
        } catch (e) {
            const saOriginal = localStorage.getItem("sa_original_token");
            if (saOriginal) {
                localStorage.setItem("rp_token", saOriginal);
                try {
                    const { data: me } = await api.get("/auth/me");
                    setUser(me);
                } catch { /* let route guard redirect */ }
            } else {
                throw e;
            }
        } finally {
            localStorage.removeItem("sa_original_token");
            localStorage.removeItem("sa_return_path");
        }
        return landing;
    }, []);

    // Phase AUTH-S — polling loop. Pauses while the tab is hidden so we don't
    // hammer the API on background tabs; resumes on `visibilitychange`. The
    // impersonation token has its own TTL/revoke surface — skip the refresh
    // logic when running impersonated (the backend will reject /refresh).
    useEffect(() => {
        if (!user) return undefined;
        const isImpersonated = !!(user && user.impersonating);
        let cancelled = false;

        const tick = async () => {
            if (cancelled) return;
            if (document.hidden) return;
            const token = localStorage.getItem("rp_token");
            if (!token) return;
            const payload = decodeJwt(token);
            if (!payload || typeof payload.exp !== "number") return;
            const expMs = payload.exp * 1000;
            const remaining = expMs - Date.now();
            if (remaining <= -HARD_EXPIRED_GRACE_MS) {
                // Already expired — let the next protected call drop us out.
                setSessionWarning(false);
                return;
            }
            if (remaining <= WARNING_THRESHOLD_MS) {
                if (!isImpersonated) setSessionWarning(true);
                return;
            }
            // Otherwise — if remaining is under the refresh threshold, slide.
            if (remaining <= REFRESH_THRESHOLD_MS && !isImpersonated) {
                await refreshSession();
            }
        };

        // Fire once on mount so users who land on the app with a token that's
        // already inside the warning window see the modal immediately.
        tick();
        const id = setInterval(tick, POLL_INTERVAL_MS);
        const onVis = () => { if (!document.hidden) tick(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            cancelled = true;
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [user, refreshSession]);

    return (
        <AuthContext.Provider value={{
            user, loading, login, signup, logout,
            refreshUser, setUser,
            startImpersonation, stopImpersonation,
            // Phase AUTH-S
            sessionWarning, setSessionWarning,
            refreshSession,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
